import React, { useState } from "react";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { Copy, Check } from "lucide-react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import type { AssistantArtifactPart } from "../../../chat/types";
import { AnimatedCard } from "../../Common/AnimatedCard";
import { ArtifactExportMenu } from "./ArtifactExportMenu";

/** Code exports as the server's rendered HTML (syntax-highlighted) or a PDF of
 *  it. Not DOCX/PPTX/XLSX: a source file in a word processor is a worse
 *  artifact than the file itself, and Copy already covers "I want the text". */
const EXPORT_FORMATS = [
  { fmt: "html", label: "HTML" },
  { fmt: "pdf", label: "PDF" },
] as const;

export function ArtifactCodeCard({ part }: { part: AssistantArtifactPart }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    await navigator.clipboard.writeText(part.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <AnimatedCard variant="sm" sx={{ my: 1, p: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        {part.title && (
          <Typography variant="caption" sx={{ flex: 1 }}>
            {part.title}
          </Typography>
        )}
        {part.language && <Chip size="small" label={part.language} />}
        {part.status === "streaming" && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
            }}
          >
            streaming…
          </Typography>
        )}
        {part.status === "failed" && (
          <Typography variant="caption" color="error">
            failed{part.error ? `: ${part.error}` : ""}
          </Typography>
        )}
        <ArtifactExportMenu
          artifactId={part.artifact_id}
          title={part.title}
          formats={EXPORT_FORMATS}
          disabled={part.status !== "complete"}
        />
        <Tooltip title={copied ? "Copied" : "Copy"}>
          <span>
            <IconButton
              size="small"
              data-ph="copy"
              onClick={onCopy}
              disabled={part.status === "streaming"}
              aria-label="copy"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box sx={{ "& pre": { my: 0 } }}>
        {/* react-syntax-highlighter has React-18-only types in some paths; cast for safety. */}
        {React.createElement(
          SyntaxHighlighter as any,
          { language: part.language ?? "text", style: oneDark },
          part.text,
        )}
      </Box>
    </AnimatedCard>
  );
}
